import { useState } from 'react';
import { ShieldCheck, Phone } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useAuthStore } from '@/store/auth.store';
import {
  useGenerateSupportCode,
  useSupportVerificationConfig,
  useUpdateSupportVerificationConfig,
  type SupportCallReason,
} from '@/api/hooks/useSettings';

const CALL_REASONS: { value: SupportCallReason; label: string }[] = [
  { value: 'billing', label: 'Billing' },
  { value: 'account_access', label: 'Account access' },
  { value: 'compliance', label: 'Compliance' },
  { value: 'vendor_assessment', label: 'Vendor assessment' },
  { value: 'other', label: 'Other' },
];

const ROLE_OPTIONS = [
  { name: 'super_admin', label: 'Super Admin' },
  { name: 'risk_manager', label: 'Risk Manager' },
  { name: 'analyst', label: 'Analyst' },
  { name: 'executive_viewer', label: 'Executive Viewer' },
  { name: 'member', label: 'Member' },
];

// ── Two-step generate flow (step-state extension of the existing Dialog) ─────────
function GenerateCodeDialog({ onClose }: { onClose: () => void }) {
  const [step, setStep] = useState<'confirm' | 'reason' | 'code' | 'denied'>('confirm');
  const [reason, setReason] = useState<SupportCallReason>('billing');
  const [code, setCode] = useState<string>('');
  const generate = useGenerateSupportCode();

  function doGenerate() {
    generate.mutate(
      { confirmed: true, call_reason: reason },
      {
        onSuccess: (r) => { setCode(r.code); setStep('code'); },
        onError: (err: any) => {
          if (err?.response?.status === 403) { setStep('denied'); }
          else { toast.error('Failed to generate support code'); }
        },
      },
    );
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Generate Support Verification Code</DialogTitle>
        </DialogHeader>

        {step === 'confirm' && (
          <div className="space-y-4">
            <p className="text-sm text-text-secondary">
              Only generate a code when you are <strong>actively on a call with VeriGRC Support</strong>.
              The code lets support verify you before taking sensitive action on your account.
            </p>
            <p className="text-sm font-medium text-text-primary">Are you currently on a call with VeriGRC Support?</p>
            <div className="flex justify-between pt-2 border-t border-border-subtle">
              <Button variant="ghost" onClick={onClose}>No, cancel</Button>
              <Button onClick={() => setStep('reason')}>Yes, continue</Button>
            </div>
          </div>
        )}

        {step === 'reason' && (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>What is the call about?</Label>
              <select
                value={reason}
                onChange={(e) => setReason(e.target.value as SupportCallReason)}
                data-testid="call-reason-select"
                className="w-full rounded-md border border-border-subtle bg-bg-surface-2 px-3 py-2 text-sm text-text-primary focus:outline-none"
              >
                {CALL_REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
              <p className="text-xs text-text-secondary">For our records only — it does not limit what support can help with.</p>
            </div>
            <div className="flex justify-between pt-2 border-t border-border-subtle">
              <Button variant="ghost" onClick={onClose}>Cancel</Button>
              <Button onClick={doGenerate} disabled={generate.isPending}>Generate code</Button>
            </div>
          </div>
        )}

        {step === 'code' && (
          <div className="space-y-4">
            <p className="text-sm text-text-secondary">Read this code to the support agent:</p>
            <div className="text-center">
              <span data-testid="support-code" className="font-mono text-3xl tracking-widest text-text-primary">{code}</span>
            </div>
            <p className="text-xs text-accent-amber bg-accent-amber/10 border border-accent-amber/30 rounded px-3 py-2">
              This code expires in 10 minutes and will not be shown again. Generate a new one if it expires.
            </p>
            <div className="flex justify-end pt-2 border-t border-border-subtle">
              <Button onClick={onClose}>Done</Button>
            </div>
          </div>
        )}

        {step === 'denied' && (
          <div className="space-y-4">
            <p className="text-sm text-text-primary" data-testid="generate-denied">
              You do not have permission to generate a support verification code. Please contact your administrator.
            </p>
            <div className="flex justify-end pt-2 border-t border-border-subtle">
              <Button onClick={onClose}>Close</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── Config: which roles may generate (super_admin locked on) ─────────────────────
function GeneratorRolesConfig() {
  const { data, isLoading } = useSupportVerificationConfig();
  const update = useUpdateSupportVerificationConfig();
  const [selected, setSelected] = useState<Set<string> | null>(null);

  // initialize from server once loaded
  const current = selected ?? new Set<string>(data?.generator_roles ?? ['super_admin']);

  if (isLoading) return <Skeleton className="h-40 rounded-xl" />;

  function toggle(name: string) {
    if (name === 'super_admin') return; // locked
    const next = new Set(current);
    if (next.has(name)) next.delete(name); else next.add(name);
    next.add('super_admin'); // lock-out guard
    setSelected(next);
  }

  function save() {
    const roles = Array.from(current);
    if (!roles.includes('super_admin')) { toast.error('Super Admin must remain able to generate codes'); return; }
    update.mutate(roles, {
      onSuccess: () => toast.success('Generator roles updated'),
      onError: () => toast.error('Failed to update generator roles'),
    });
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-text-secondary">Choose which roles may generate a support verification code.</p>
      <div className="space-y-2">
        {ROLE_OPTIONS.map((r) => {
          const locked = r.name === 'super_admin';
          return (
            <label key={r.name} className="flex items-center gap-2 text-sm text-text-primary">
              <input
                type="checkbox"
                checked={current.has(r.name)}
                disabled={locked}
                onChange={() => toggle(r.name)}
                data-testid={`role-${r.name}`}
              />
              <span>{r.label}</span>
              {locked && <span className="text-xs text-text-secondary">(always allowed)</span>}
            </label>
          );
        })}
      </div>
      <div className="pt-2 border-t border-border-subtle">
        <Button size="sm" onClick={save} disabled={update.isPending || !selected}>Save</Button>
      </div>
    </div>
  );
}

export function SupportVerificationPage() {
  const { hasAnyRole } = useAuthStore();
  const isAdmin = hasAnyRole(['super_admin']);
  const [generating, setGenerating] = useState(false);

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h1 className="text-2xl font-bold text-text-primary">Support Verification</h1>
        <p className="mt-1 text-sm text-text-secondary">Generate a one-time code to verify your identity when contacting VeriGRC Support.</p>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2"><Phone className="h-4 w-4 text-text-secondary" /> Generate a code</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-text-secondary mb-3">Generate a 10-minute, single-use code to read to the support agent on your call.</p>
          <Button onClick={() => setGenerating(true)}>Generate Support Code</Button>
        </CardContent>
      </Card>

      {isAdmin && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-text-secondary" /> Who can generate codes</CardTitle>
          </CardHeader>
          <CardContent>
            <GeneratorRolesConfig />
          </CardContent>
        </Card>
      )}

      {generating && <GenerateCodeDialog onClose={() => setGenerating(false)} />}
    </div>
  );
}
